import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState, type InputHTMLAttributes } from 'react';
import { attributes as attributeContracts } from '@bb/contracts';
import { Button, Checkbox, Field, Input, Select, type SelectOption } from '@bb/ui';
import type { AdminApi, CampaignSummary } from '../../../api/client.ts';
import { OPERATOR_LABEL } from '../../audience/FilterRow.tsx';
import { EventPicker } from '../../catalog/EventPicker.tsx';
import {
  conditionTypeOf,
  emptyCondition,
  equalsTo,
  issuesAt,
  issuesUnder,
  MAX_PROPERTY_FILTERS,
  newCondition,
  operandKind,
  operatorsFor,
  triggerTemplate,
  withConditionOperator,
  type ActionBasedIssue,
  type AttributeTarget,
  type ConditionType,
  type PropertyCondition,
  type TriggerKind,
  type TriggerSpec,
} from './actionBased.ts';
import { NumberInput } from './NumberInput.tsx';

const useApi = () => (useRouter().options.context as { api: AdminApi }).api;

export const TRIGGER_KINDS: Array<{ value: TriggerKind; label: string }> = [
  { value: 'custom_event', label: 'Performs a custom event' },
  { value: 'session_start', label: 'Starts a session' },
  { value: 'attribute_changed', label: 'Changes an attribute value' },
  { value: 'subscription_changed', label: 'Changes a subscription group state' },
  { value: 'message_interaction', label: 'Interacts with another campaign' },
];
const INTERACTIONS: SelectOption[] = [
  { value: 'click', label: 'Clicks a message' },
  { value: 'open', label: 'Opens a message' },
  { value: 'impression', label: 'Sees a message (impression)' },
];
const GROUP_STATES: SelectOption[] = [
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
];
const CAMPAIGN_STATUS: Record<CampaignSummary['status'] | 'idle', string> = { draft: 'Draft', scheduled: 'Scheduled', active: 'Active', idle: 'Idle', paused: 'Paused', stopped: 'Stopped', completed: 'Completed', archived: 'Archived' };

type Path = Array<string | number>;
interface Common {
  environmentId: string;
  readOnly: boolean;
  issues: readonly ActionBasedIssue[];
  /** Path of this part in the action-based value, for its issues. */
  path: Path;
}
type FieldsProps<K extends TriggerKind> = Common & { spec: Extract<TriggerSpec, { kind: K }>; onChange: (next: TriggerSpec) => void };

function Errors({ messages }: { messages: string[] }) {
  return (
    <>
      {messages.map((message) => (
        <p key={message} className="bb-field__error" role="alert">
          {message}
        </p>
      ))}
    </>
  );
}

/** Comma-separated values; keeps the typed text (a trailing comma) while the list it means stays the same. */
function ListInput({ value, onValueChange, ...rest }: { value: string[]; onValueChange: (value: string[]) => void } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const parse = (text: string) =>
    text
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  const [text, setText] = useState(() => value.join(', '));
  const [synced, setSynced] = useState(value);
  if (JSON.stringify(value) !== JSON.stringify(synced)) {
    setSynced(value);
    if (JSON.stringify(parse(text)) !== JSON.stringify(value)) setText(value.join(', '));
  }
  return (
    <Input
      {...rest}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onValueChange(parse(e.target.value));
      }}
    />
  );
}

const withNumber = (condition: PropertyCondition, key: 'value' | 'value2', n: number | undefined): PropertyCondition => {
  const { [key]: _old, ...rest } = condition;
  return n === undefined ? rest : { ...rest, [key]: n };
};

function OperandInputs({ condition, err, readOnly, onChange }: { condition: PropertyCondition; err: (field: string) => string | undefined; readOnly: boolean; onChange: (next: PropertyCondition) => void }) {
  const value = condition.value;
  const number = typeof value === 'number' ? value : undefined;
  switch (operandKind(condition.dataType, condition.operator)) {
    case 'none':
      return null;
    case 'number':
      return (
        <Field label="Value" error={err('value')}>
          <NumberInput step="any" value={number} disabled={readOnly} onValueChange={(n) => onChange(withNumber(condition, 'value', n))} />
        </Field>
      );
    case 'days':
      return (
        <Field label="Days" error={err('value')}>
          <NumberInput min={0} max={3650} step={1} value={number} disabled={readOnly} onValueChange={(n) => onChange(withNumber(condition, 'value', n))} />
        </Field>
      );
    case 'range':
      return (
        <>
          <Field label="From" error={err('value')}>
            <NumberInput step="any" value={number} disabled={readOnly} onValueChange={(n) => onChange(withNumber(condition, 'value', n))} />
          </Field>
          <Field label="To" error={err('value2')}>
            <NumberInput step="any" value={condition.value2} disabled={readOnly} onValueChange={(n) => onChange(withNumber(condition, 'value2', n))} />
          </Field>
        </>
      );
    case 'list':
      return (
        <Field label="Values" hint="Separate values with commas." error={err('value')}>
          <ListInput value={Array.isArray(value) ? value.map(String) : []} disabled={readOnly} placeholder="UA, PL" onValueChange={(list) => onChange({ ...condition, value: list })} />
        </Field>
      );
    case 'date':
      return (
        <Field label="Date" error={err('value')}>
          <Input type="date" value={typeof value === 'string' ? value.slice(0, 10) : ''} disabled={readOnly} onChange={(e) => onChange({ ...condition, value: e.target.value })} />
        </Field>
      );
    case 'text':
      return (
        <Field label="Value" error={err('value')}>
          <Input value={typeof value === 'string' ? value : ''} disabled={readOnly} onChange={(e) => onChange({ ...condition, value: e.target.value })} />
        </Field>
      );
  }
}

interface RegistryProperty {
  path: string;
  type: ConditionType;
}

/** One condition on a property of the event: the property (its type from the registry), the operator and the operand. */
function PropertyFilterEditor({ label, condition, properties, path, issues, readOnly, onChange, onRemove }: Omit<Common, 'environmentId'> & { label: string; condition: PropertyCondition; properties: RegistryProperty[]; onChange: (next: PropertyCondition) => void; onRemove: () => void }) {
  const err = (field: string) => issuesAt(issues, ...path, field)[0];
  const known = properties.find((p) => p.path === condition.path);
  const options: SelectOption[] = properties.map((p) => ({ value: p.path, label: `${p.path} (${p.type})` }));
  if (condition.path && !known) options.unshift({ value: condition.path, label: `${condition.path} (not in the registry)` });
  // Text properties holding ISO times can be compared as times.
  const asTime = (known?.type ?? condition.dataType) === 'string' || condition.dataType === 'time';
  return (
    <fieldset className="audience-filter">
      <legend>{label}</legend>
      <Field label="Property" error={err('path')}>
        <Select
          placeholder="Choose a property"
          options={options}
          value={condition.path}
          disabled={readOnly}
          onChange={(e) => {
            const chosen = properties.find((p) => p.path === e.target.value);
            if (chosen) onChange(newCondition(chosen.path, chosen.type));
          }}
        />
      </Field>
      {condition.path && asTime && (
        <Field label="Compare as" error={err('dataType')}>
          <Select
            options={[
              { value: 'string', label: 'Text' },
              { value: 'time', label: 'Date and time' },
            ]}
            value={condition.dataType}
            disabled={readOnly}
            onChange={(e) => onChange(newCondition(condition.path, e.target.value as ConditionType))}
          />
        </Field>
      )}
      {condition.path && (
        <>
          <Field label="Operator" error={err('operator')}>
            <Select options={operatorsFor(condition.dataType).map((op) => ({ value: op, label: OPERATOR_LABEL[op] ?? op }))} value={condition.operator} disabled={readOnly} onChange={(e) => onChange(withConditionOperator(condition, e.target.value))} />
          </Field>
          <OperandInputs condition={condition} err={err} readOnly={readOnly} onChange={onChange} />
        </>
      )}
      <Errors messages={issuesAt(issues, ...path)} />
      {!readOnly && (
        <Button size="sm" variant="ghost" aria-label={`Remove ${label.toLowerCase()}`} onClick={onRemove}>
          Remove
        </Button>
      )}
    </fieldset>
  );
}

/** A custom event from the registry and its property filters; shared with conversion events (BB-103). */
export function CustomEventFields({ spec, onChange, path, issues, readOnly, environmentId }: FieldsProps<'custom_event'>) {
  const api = useApi();
  const events = useQuery({ queryKey: ['event-picker', environmentId], queryFn: () => api.eventPicker(environmentId, '') });
  const chosen = events.data?.find((e) => e.name === spec.event);
  const properties = (chosen?.properties ?? []).flatMap((p): RegistryProperty[] => {
    const type = conditionTypeOf(p.dataType);
    return type ? [{ path: p.path, type }] : [];
  });
  const eventError = issuesAt(issues, ...path, 'event')[0];
  const filters = spec.propertyFilters;
  const setFilters = (next: PropertyCondition[]) => onChange({ ...spec, propertyFilters: next });
  return (
    <>
      {readOnly ? (
        <Field label="Event" error={eventError}>
          <Input value={spec.event} disabled readOnly />
        </Field>
      ) : (
        <>
          <EventPicker
            environmentId={environmentId}
            value={spec.event || null}
            onChange={(name) => {
              // Properties differ per event: the filters of another event never carry over.
              if ((name ?? '') !== spec.event) onChange({ kind: 'custom_event', event: name ?? '', propertyFilters: [] });
            }}
          />
          {eventError && (
            <p className="bb-field__error" role="alert">
              {eventError}
            </p>
          )}
        </>
      )}
      {spec.event && events.isSuccess && !chosen && <p className="notice notice--warning">{spec.event} is not in the event registry or is blocked: it may never arrive.</p>}
      <div role="group" aria-label="Property filters">
        {filters.map((condition, j) => (
          <PropertyFilterEditor
            key={j}
            label={`Property filter ${j + 1}`}
            condition={condition}
            properties={properties}
            path={[...path, 'propertyFilters', j]}
            issues={issues}
            readOnly={readOnly}
            onChange={(next) => setFilters(filters.map((c, k) => (k === j ? next : c)))}
            onRemove={() => setFilters(filters.filter((_, k) => k !== j))}
          />
        ))}
        <Errors messages={issuesAt(issues, ...path, 'propertyFilters')} />
        {filters.length > 1 && <p className="hint">The event has to match every filter.</p>}
        {chosen && properties.length === 0 && <p className="hint">The registry has no properties of this event to filter on.</p>}
        {!readOnly && (
          <Button size="sm" disabled={!spec.event || filters.length >= MAX_PROPERTY_FILTERS} onClick={() => setFilters([...filters, emptyCondition()])}>
            Add a property filter
          </Button>
        )}
      </div>
    </>
  );
}

function SessionStartFields({ spec, onChange, path, issues, readOnly, environmentId }: FieldsProps<'session_start'>) {
  const api = useApi();
  const apps = useQuery({ queryKey: ['apps', environmentId], queryFn: () => api.listApps(environmentId) });
  const chosen = spec.appInstanceIds ?? [];
  const listed = (apps.data ?? []).filter((a) => a.status === 'active' || chosen.includes(a.id));
  const unknown = apps.isSuccess ? chosen.filter((id) => !listed.some((a) => a.id === id)) : [];
  const toggle = (id: string, on: boolean) => {
    const next = on ? [...chosen, id] : chosen.filter((x) => x !== id);
    onChange(next.length ? { kind: 'session_start', appInstanceIds: next } : { kind: 'session_start' });
  };
  return (
    <fieldset>
      <legend>Apps</legend>
      <p className="hint">{chosen.length ? 'A session in one of the chosen apps.' : 'No app chosen: a session in any app.'}</p>
      {listed.map((a) => (
        <Checkbox key={a.id} label={a.name} checked={chosen.includes(a.id)} disabled={readOnly} onCheckedChange={(on) => toggle(a.id, on)} />
      ))}
      {unknown.map((id) => (
        <Checkbox key={id} label={`Unknown app ${id}`} checked disabled={readOnly} onCheckedChange={(on) => toggle(id, on)} />
      ))}
      {apps.isSuccess && listed.length === 0 && <p className="hint">This environment has no apps yet.</p>}
      <Errors messages={issuesUnder(issues, ...path, 'appInstanceIds').slice(0, 1)} />
    </fieldset>
  );
}

function AttributeValue({ type, target, error, readOnly, onChange }: { type: string; target: Extract<AttributeTarget, { kind: 'equals' }>; error: string | undefined; readOnly: boolean; onChange: (next: AttributeTarget) => void }) {
  const value = target.value as string | number | boolean | undefined;
  if (type === 'number')
    return (
      <Field label="New value" error={error}>
        <NumberInput step="any" value={typeof value === 'number' ? value : undefined} disabled={readOnly} onValueChange={(n) => onChange(equalsTo(n))} />
      </Field>
    );
  if (type === 'boolean')
    return (
      <Field label="New value" error={error}>
        <Select
          options={[
            { value: 'true', label: 'true' },
            { value: 'false', label: 'false' },
          ]}
          value={String(value === true)}
          disabled={readOnly}
          onChange={(e) => onChange(equalsTo(e.target.value === 'true'))}
        />
      </Field>
    );
  return (
    <Field label="New value" error={error}>
      <Input value={value === undefined ? '' : String(value)} disabled={readOnly} onChange={(e) => onChange(equalsTo(e.target.value))} />
    </Field>
  );
}

const defaultEquals = (type: string): AttributeTarget => equalsTo(type === 'number' ? undefined : type === 'boolean' ? true : '');
/** Arrays and objects never equal a single value: they can only fire on any change. */
const comparable = (type: string) => type !== 'array' && type !== 'object';

function AttributeChangedFields({ spec, onChange, path, issues, readOnly, environmentId }: FieldsProps<'attribute_changed'>) {
  const api = useApi();
  const registry = useQuery({ queryKey: ['attributes', environmentId, 'trigger'], queryFn: () => api.listAttributes(environmentId, {}) });
  const items = registry.data?.items ?? [];
  const all = [
    ...attributeContracts.STANDARD_ATTRIBUTES.map((a) => ({ key: a.key as string, type: items.find((r) => r.key === a.key)?.dataType ?? 'string', standard: true })),
    ...items.filter((a) => a.kind === 'custom' && a.status !== 'blocked').map((a) => ({ key: a.key, type: a.dataType, standard: false })),
  ];
  const chosen = all.find((a) => a.key === spec.attribute);
  const options: SelectOption[] = all.map((a) => ({ value: a.key, label: a.standard ? `${a.key} (standard)` : `${a.key} (${a.type})` }));
  if (spec.attribute && !chosen) options.unshift({ value: spec.attribute, label: registry.isPending ? spec.attribute : `${spec.attribute} (not in the registry)` });
  const type = chosen?.type ?? 'string';
  const err = (...rest: Path) => issuesAt(issues, ...path, ...rest)[0];
  return (
    <>
      <Field label="Attribute" error={err('attribute')}>
        <Select
          placeholder="Choose an attribute"
          options={options}
          value={spec.attribute}
          disabled={readOnly}
          onChange={(e) => {
            const next = all.find((a) => a.key === e.target.value)?.type ?? 'string';
            onChange({ ...spec, attribute: e.target.value, to: spec.to.kind === 'equals' && comparable(next) ? defaultEquals(next) : { kind: 'any' } });
          }}
        />
      </Field>
      <Field label="Changes to" error={err('to')}>
        <Select
          options={[
            { value: 'any', label: 'Any new value' },
            { value: 'equals', label: 'A specific value', disabled: !comparable(type) },
          ]}
          value={spec.to.kind}
          disabled={readOnly}
          onChange={(e) => onChange({ ...spec, to: e.target.value === 'equals' ? defaultEquals(type) : { kind: 'any' } })}
        />
      </Field>
      {spec.to.kind === 'equals' && <AttributeValue type={type} target={spec.to} error={err('to', 'value')} readOnly={readOnly} onChange={(to) => onChange({ ...spec, to })} />}
      <p className="hint">Fires only when the value really changes, not when the same value is sent again.</p>
    </>
  );
}

function SubscriptionChangedFields({ spec, onChange, path, issues, readOnly, environmentId }: FieldsProps<'subscription_changed'>) {
  const api = useApi();
  const groups = useQuery({ queryKey: ['subscription-groups', environmentId, 'audience'], queryFn: () => api.listSubscriptionGroups(environmentId, { status: 'active' }) });
  const options: SelectOption[] = (groups.data ?? []).map((g) => ({ value: g.id, label: g.name }));
  if (spec.groupId && groups.isSuccess && !options.some((o) => o.value === spec.groupId)) options.unshift({ value: spec.groupId, label: `Unknown or archived group ${spec.groupId}` });
  const err = (field: string) => issuesAt(issues, ...path, field)[0];
  return (
    <>
      <Field label="Subscription group" error={err('groupId')}>
        <Select placeholder={groups.isPending ? 'Loading groups…' : 'Choose a group'} options={options} value={spec.groupId} disabled={readOnly} onChange={(e) => onChange({ ...spec, groupId: e.target.value })} />
      </Field>
      <Field label="New state" error={err('to')}>
        <Select options={GROUP_STATES} value={spec.to} disabled={readOnly} onChange={(e) => onChange({ ...spec, to: e.target.value as 'subscribed' | 'unsubscribed' })} />
      </Field>
    </>
  );
}

function MessageInteractionFields({ spec, onChange, path, issues, readOnly, environmentId }: FieldsProps<'message_interaction'>) {
  const api = useApi();
  const campaigns = useQuery({ queryKey: ['campaigns', environmentId, 'picker'], queryFn: () => api.listCampaigns(environmentId, { status: 'all', sort: 'name', limit: 200 }) });
  const options: SelectOption[] = (campaigns.data?.items ?? [])
    .filter((c) => c.status !== 'archived' || c.apiId === spec.messageApiId)
    .map((c) => ({ value: c.apiId, label: `${c.name} · ${CAMPAIGN_STATUS[c.idle ? 'idle' : c.status]}` }));
  if (spec.messageApiId && campaigns.isSuccess && !options.some((o) => o.value === spec.messageApiId)) options.unshift({ value: spec.messageApiId, label: `Unknown campaign ${spec.messageApiId}` });
  const err = (field: string) => issuesAt(issues, ...path, field)[0];
  return (
    <>
      <Field label="Campaign" error={err('messageApiId')}>
        <Select placeholder={campaigns.isPending ? 'Loading campaigns…' : 'Choose a campaign'} options={options} value={spec.messageApiId} disabled={readOnly} onChange={(e) => onChange({ ...spec, messageApiId: e.target.value })} />
      </Field>
      <Field label="Interaction" error={err('interaction')}>
        <Select options={INTERACTIONS} value={spec.interaction} disabled={readOnly} onChange={(e) => onChange({ ...spec, interaction: e.target.value as 'click' | 'open' | 'impression' })} />
      </Field>
    </>
  );
}

export type TriggerEditorProps = Common & { label: string; spec: TriggerSpec; onChange: (next: TriggerSpec) => void; onRemove: (() => void) | undefined };

/** One trigger: what the user does and the details of that action; also a Canvas exit event (BB-112). */
export function TriggerEditor({ label, spec, onChange, onRemove, ...common }: TriggerEditorProps) {
  const { issues, path, readOnly } = common;
  return (
    <fieldset className="audience-group">
      <legend>{label}</legend>
      <Field label="Action" error={issuesAt(issues, ...path, 'kind')[0]}>
        <Select options={TRIGGER_KINDS} value={spec.kind} disabled={readOnly} onChange={(e) => onChange(triggerTemplate(e.target.value as TriggerKind))} />
      </Field>
      {spec.kind === 'custom_event' && <CustomEventFields {...common} spec={spec} onChange={onChange} />}
      {spec.kind === 'session_start' && <SessionStartFields {...common} spec={spec} onChange={onChange} />}
      {spec.kind === 'attribute_changed' && <AttributeChangedFields {...common} spec={spec} onChange={onChange} />}
      {spec.kind === 'subscription_changed' && <SubscriptionChangedFields {...common} spec={spec} onChange={onChange} />}
      {spec.kind === 'message_interaction' && <MessageInteractionFields {...common} spec={spec} onChange={onChange} />}
      <Errors messages={issuesAt(issues, ...path)} />
      {!readOnly && onRemove && (
        <Button size="sm" variant="ghost" aria-label={`Remove ${label.toLowerCase()}`} onClick={onRemove}>
          Remove
        </Button>
      )}
    </fieldset>
  );
}

export interface TriggerListProps {
  environmentId: string;
  readOnly: boolean;
  issues: readonly ActionBasedIssue[];
  /** Where the list sits in the value: the triggers or the cancel events. */
  list: 'triggers' | 'cancelOn';
  items: TriggerSpec[];
  onChange: (next: TriggerSpec[]) => void;
  /** Label of one item, numbered: «Trigger 1». */
  itemLabel: string;
  addLabel: string;
  min: number;
  max: number;
}

/** Triggers that each fire on their own (OR), edited one by one. */
export function TriggerList({ environmentId, readOnly, issues, list, items, onChange, itemLabel, addLabel, min, max }: TriggerListProps) {
  return (
    <>
      {items.length > 0 && (
        <ol className="plain-list audience-builder">
          {items.map((spec, index) => (
            <li key={index}>
              {index > 0 && (
                <p className="hint" aria-hidden="true">
                  or
                </p>
              )}
              <TriggerEditor
                label={`${itemLabel} ${index + 1}`}
                spec={spec}
                environmentId={environmentId}
                readOnly={readOnly}
                issues={issues}
                path={[list, index]}
                onChange={(next) => onChange(items.map((t, i) => (i === index ? next : t)))}
                onRemove={items.length > min ? () => onChange(items.filter((_, i) => i !== index)) : undefined}
              />
            </li>
          ))}
        </ol>
      )}
      <Errors messages={issuesAt(issues, list)} />
      {!readOnly && (
        <Button size="sm" disabled={items.length >= max} onClick={() => onChange([...items, triggerTemplate('custom_event')])}>
          {addLabel}
        </Button>
      )}
    </>
  );
}
